import React, { useState, useEffect, useMemo } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import {
  Info,
  TrendingUp,
  PiggyBank,
  Wallet,
  Calculator,
  ChevronDown,
  ChevronUp,
  ShieldAlert,
  Zap,
  Settings,
  HelpCircle,
  Sparkles,
  ArrowRight,
  Coins,
  Repeat,
  Hammer,
  ArrowRightLeft,
  RefreshCw,
  BarChart3,
  Scale,
  ScrollText,
  Building,
  AlertCircle,
  Percent,
  ExternalLink,
  FileText,
  X,
  Cookie,
  Check,
} from 'lucide-react';

// --- DATA: ETF PRESETS ---
const ETF_PRESETS = [
  { id: 'custom', name: 'Personalizzato (Inserisci tu)', ret: 7.0, ter: 0.2 },
  { id: 'sp500', name: 'S&P 500 (USA - Storico alto)', ret: 10.0, ter: 0.07 },
  {
    id: 'msci_world',
    name: 'MSCI World (Globale Sviluppati)',
    ret: 8.5,
    ter: 0.2,
  },
  { id: 'vwce', name: 'Vanguard All-World (VWCE)', ret: 8.0, ter: 0.22 },
  { id: 'nasdaq', name: 'Nasdaq 100 (Tech)', ret: 12.0, ter: 0.2 },
  { id: 'eurostoxx', name: 'Euro Stoxx 50 (Europa)', ret: 6.5, ter: 0.15 },
  {
    id: 'conservative',
    name: 'Portafoglio Bilanciato (60/40)',
    ret: 5.5,
    ter: 0.25,
  },
];

// --- HELPER COMPONENTS ---

// 1. Tooltip
const InfoTooltip = ({ text }) => (
  <div className="group relative inline-block ml-1.5 align-middle print:hidden">
    <HelpCircle
      size={15}
      className="text-blue-400 hover:text-blue-600 cursor-help transition-colors"
    />
    <div className="invisible group-hover:visible absolute z-50 w-80 p-4 mt-2 -ml-40 text-xs text-white bg-slate-800 rounded-xl shadow-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none border border-slate-700 leading-relaxed z-[100] text-left">
      <div className="font-bold text-blue-200 mb-1 flex items-center gap-2">
        <Info size={12} /> Spiegazione:
      </div>
      {text}
      <div className="absolute bottom-full left-1/2 -ml-1 border-4 border-transparent border-b-slate-800"></div>
    </div>
  </div>
);

// 2. Affiliate Card (NEW)
const AffiliateCard = ({ title, text, link, color = 'blue' }) => {
  const colors = {
    blue: 'bg-blue-50 border-blue-100 text-blue-800 hover:bg-blue-100',
    emerald:
      'bg-emerald-50 border-emerald-100 text-emerald-800 hover:bg-emerald-100',
    amber: 'bg-amber-50 border-amber-100 text-amber-800 hover:bg-amber-100',
  };

  return (
    <a
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      className={`block mt-3 p-3 rounded-lg border transition-all flex items-start gap-3 group print:hidden ${colors[color]}`}
    >
      <ExternalLink
        size={16}
        className="mt-0.5 shrink-0 opacity-70 group-hover:opacity-100"
      />
      <div>
        <p className="text-[10px] font-bold uppercase opacity-80 tracking-wide">
          {title}
        </p>
        <p className="text-xs font-medium underline decoration-dotted underline-offset-2">
          {text}
        </p>
      </div>
    </a>
  );
};

// 3. Smart Input
const NumberInput = ({
  value,
  onChange,
  label,
  symbol,
  step = 1,
  placeholder = '0',
  className = '',
}) => {
  const isEmpty = value === '' || value === null;
  const handleChange = (e) => {
    const val = e.target.value;
    onChange(val === '' ? '' : Number(val));
  };
  return (
    <div className={className}>
      {label && (
        <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1.5">
          {label}
        </label>
      )}
      <div className="relative group">
        {symbol && (
          <span
            className={`absolute left-3 top-2.5 transition-colors ${
              isEmpty ? 'text-rose-300' : 'text-slate-400'
            }`}
          >
            {symbol}
          </span>
        )}
        <input
          type="number"
          step={step}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          className={`w-full p-2.5 text-sm border rounded-lg outline-none transition-all font-semibold ${
            symbol ? 'pl-8' : ''
          } ${
            isEmpty
              ? 'border-rose-400 focus:ring-2 focus:ring-rose-200 bg-rose-50 text-rose-700 placeholder-rose-300'
              : 'border-slate-200 focus:ring-2 focus:ring-blue-500 focus:bg-white bg-slate-50 text-slate-700'
          }`}
        />
        {isEmpty && (
          <div className="absolute right-3 top-2.5 text-rose-500 pointer-events-none animate-pulse">
            <AlertCircle size={16} />
          </div>
        )}
      </div>
    </div>
  );
};

// 4. Fiscal Deep Dive
const FiscalDeepDive = ({ simulation, cedolare, isFirstHome }) => {
  const [isOpen, setIsOpen] = useState(true);
  const f = (n) =>
    new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(n);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mt-6 print:break-inside-avoid">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-5 bg-slate-50 border-b border-slate-100 hover:bg-slate-100 transition-colors print:bg-white"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 text-indigo-600 rounded-lg print:border print:border-indigo-200">
            <Scale size={20} />
          </div>
          <div className="text-left">
            <h3 className="font-bold text-slate-800 text-sm">
              Focus Fiscale & Costi Nascosti
            </h3>
            <p className="text-xs text-slate-500">
              Analisi dettagliata di Tasse, IMU e Spese Finanziarie
            </p>
          </div>
        </div>
        {isOpen ? (
          <ChevronUp size={18} className="text-slate-400" />
        ) : (
          <ChevronDown size={18} className="text-slate-400" />
        )}
      </button>

      {isOpen && (
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
          <div className="space-y-4">
            <h4 className="font-bold text-slate-700 flex items-center gap-2 uppercase text-xs tracking-wider border-b border-slate-100 pb-2">
              <ScrollText size={14} className="text-blue-500" /> Fase di
              Acquisto
            </h4>
            <div className="group">
              <div className="flex justify-between items-center mb-1">
                <span className="font-medium text-slate-600">
                  Imposta di Registro
                </span>
                <span className="font-bold text-slate-800">
                  {f(simulation.purchaseTax)}
                </span>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">
                Tassa "Una Tantum". Calcolata sul{' '}
                <strong>Valore Catastale</strong>.{' '}
                {isFirstHome
                  ? ' Essendo Prima Casa paghi solo il 2%.'
                  : ' Essendo Seconda Casa paghi il 9%.'}
              </p>
            </div>
            <div className="group">
              <div className="flex justify-between items-center mb-1">
                <span className="font-medium text-slate-600">
                  Spese Accessorie (Notaio+Agenzia)
                </span>
                <span className="font-bold text-slate-800">
                  {f(simulation.agencyTotal + 2000 + 1000)}*
                </span>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">
                Costi "a fondo perduto" (IVA inclusa). Non li recuperi
                rivendendo la casa allo stesso prezzo.
              </p>
            </div>
          </div>
          <div className="space-y-4">
            <h4 className="font-bold text-slate-700 flex items-center gap-2 uppercase text-xs tracking-wider border-b border-slate-100 pb-2">
              <Building size={14} className="text-emerald-500" /> Mantenimento &
              Tasse
            </h4>
            <div className="group">
              <div className="flex justify-between items-center mb-1">
                <span className="font-medium text-slate-600">IMU Annuale</span>
                <span className="font-bold text-slate-800">
                  {f(simulation.data[0]?.details?.expenses - 500 - 600)} (Stima)
                </span>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">
                Tassa municipale.{' '}
                {isFirstHome
                  ? 'Sulla Prima Casa non si paga.'
                  : 'Sulla Seconda Casa è obbligatoria e pesa sul rendimento.'}
              </p>
            </div>
            <div className="group">
              <div className="flex justify-between items-center mb-1">
                <span className="font-medium text-slate-600">
                  Cedolare Secca ({cedolare}%)
                </span>
                <span className="font-bold text-slate-800">
                  {f(simulation.data[0]?.details?.tax)} / anno
                </span>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">
                {cedolare === 10
                  ? 'Canone Concordato (10%), ottimo per abbattere le tasse.'
                  : 'Canone Libero (21%), più oneroso fiscalmente.'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// 5. Educational Module
const EducationalModule = () => {
  // MODIFICA 2: Banner sempre aperto di default
  const [isOpen, setIsOpen] = useState(true);

  return (
    <div className="bg-slate-900 rounded-xl overflow-hidden shadow-lg border border-slate-700 mt-6 text-white print:hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-800 transition-colors"
      >
        <div className="flex items-center gap-2 font-bold text-sm md:text-base text-blue-200">
          <Zap className="text-yellow-400" size={18} />
          <span>Perché l'immobiliare piace ai professionisti?</span>
        </div>
        {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      {isOpen && (
        <div className="p-4 pt-0">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs md:text-sm text-slate-300 pb-4">
            <div className="bg-white/5 p-4 rounded-xl border border-white/10">
              <h4 className="font-bold text-white mb-2 flex items-center gap-2">
                1. Leva Finanziaria
              </h4>
              <p className="leading-relaxed">
                Con soli 20k controlli un asset da 100k. Un amplificatore di
                rendimento impossibile con gli ETF.
              </p>
            </div>
            <div className="bg-white/5 p-4 rounded-xl border border-white/10">
              <h4 className="font-bold text-white mb-2 flex items-center gap-2">
                2. Efficienza Fiscale
              </h4>
              <p className="leading-relaxed">
                Se vendi dopo 5 anni, il guadagno è{' '}
                <strong>100% esentasse</strong>. Con gli ETF paghi sempre il
                26%.
              </p>
            </div>
            <div className="bg-white/5 p-4 rounded-xl border border-white/10">
              <h4 className="font-bold text-white mb-2 flex items-center gap-2">
                3. Controllo Attivo
              </h4>
              <p className="leading-relaxed">
                Puoi ristrutturare per alzare il valore. Con le azioni sei
                passivo.
              </p>
            </div>
          </div>
          {/* MONETIZATION C: BOOK */}
          <AffiliateCard
            title="Lettura Consigliata"
            text="A Spasso per Wall Street - La guida per investire con successo"
            link="https://www.amazon.it/spasso-Wall-Street-vincere-investendo/dp/8820359421"
            color="amber"
          />
        </div>
      )}
    </div>
  );
};

// 6. Cookie Banner (COMPLIANT)
const CookieBanner = () => {
  const [accepted, setAccepted] = useState(true);

  useEffect(() => {
    // Check localStorage on mount
    const isAck = localStorage.getItem('cookie_ack_v2');
    if (!isAck) setAccepted(false);
  }, []);

  const handleChoice = () => {
    localStorage.setItem('cookie_ack_v2', 'true');
    setAccepted(true);
  };

  if (accepted) return null;

  return (
    <div className="fixed bottom-0 left-0 w-full bg-white text-slate-900 p-4 md:p-5 shadow-[0_-5px_20px_rgba(0,0,0,0.1)] z-[999] border-t border-slate-200 print:hidden animate-in slide-in-from-bottom-4 duration-500">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex-1">
          <p className="text-sm font-semibold mb-1 flex items-center gap-2">
            <Cookie className="text-blue-600" size={18} /> Informativa Cookie
          </p>
          <p className="text-xs text-slate-500 leading-relaxed">
            Noi e terze parti selezionate utilizziamo cookie o tecnologie simili
            per finalità tecniche e, con il tuo consenso, anche per altre
            finalità come specificato nella{' '}
            <a href="#" className="underline decoration-dotted text-blue-600">
              cookie policy
            </a>
            . Il rifiuto del consenso può rendere non disponibili le relative
            funzioni.
          </p>
        </div>
        <div className="flex gap-3 shrink-0 w-full md:w-auto">
          <button
            onClick={handleChoice}
            className="flex-1 md:flex-none bg-slate-100 hover:bg-slate-200 text-slate-700 px-5 py-2.5 rounded-lg text-xs font-bold transition-colors"
          >
            Rifiuta Tutto
          </button>
          <button
            onClick={handleChoice}
            className="flex-1 md:flex-none bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg text-xs font-bold transition-colors shadow-sm"
          >
            Accetta Tutto
          </button>
        </div>
      </div>
    </div>
  );
};

// 7. Lead Modal
const LeadModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const handlePrint = (e) => {
    e.preventDefault();
    onClose();
    setTimeout(() => window.print(), 300); // Wait for modal to close then print
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4 print:hidden">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in zoom-in-95 duration-200">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <FileText className="text-blue-600" /> Ricevi il Report
          </h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-slate-100 rounded-full"
          >
            <X size={20} className="text-slate-500" />
          </button>
        </div>
        <p className="text-sm text-slate-600 mb-4">
          Inserisci la tua email per scaricare il PDF completo con l'analisi
          fiscale dettagliata e i grafici proiettivi.
        </p>
        <form onSubmit={handlePrint} className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
              La tua Email
            </label>
            <input
              type="email"
              required
              placeholder="mario.rossi@esempio.it"
              className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>
          <div className="flex items-start gap-2">
            <input type="checkbox" required id="privacy" className="mt-1" />
            <label htmlFor="privacy" className="text-xs text-slate-500">
              Accetto la Privacy Policy e l'iscrizione alla newsletter di
              RenditaVera.
            </label>
          </div>
          <button
            type="submit"
            className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg transition-all flex items-center justify-center gap-2"
          >
            <FileText size={18} /> Scarica PDF Ora
          </button>
        </form>
      </div>
    </div>
  );
};

// --- MAIN APPLICATION ---

export default function App() {
  // --- STATE ---
  const [strategy, setStrategy] = useState('rent');
  const [savings, setSavings] = useState(60000);
  const [years, setYears] = useState(20);
  const [propertyPrice, setPropertyPrice] = useState(180000);
  const [renovationCost, setRenovationCost] = useState(20000);
  const [renditaCatastale, setRenditaCatastale] = useState(600);
  const [isFirstHome, setIsFirstHome] = useState(false);
  const [agencyRate, setAgencyRate] = useState(3);
  const [notaryFee, setNotaryFee] = useState(2000);
  const [bankFees, setBankFees] = useState(1000);
  const [downPaymentPerc, setDownPaymentPerc] = useState(20);
  const [mortgageRate, setMortgageRate] = useState(3.0);
  const [mortgageYears, setMortgageYears] = useState(25);
  const [monthlyRent, setMonthlyRent] = useState(850);
  const [condoFees, setCondoFees] = useState(50);
  const [maintenanceYearly, setMaintenanceYearly] = useState(500);
  const [cedolareSecca, setCedolareSecca] = useState(21);
  const [imuYearly, setImuYearly] = useState(0);
  const [tenantRiskMonths, setTenantRiskMonths] = useState(6);
  const [propertyAppreciation, setPropertyAppreciation] = useState(1.0);
  const [inflation, setInflation] = useState(2.0);
  const [applyIstat, setApplyIstat] = useState(true);
  const [etfReturn, setEtfReturn] = useState(7.0);
  const [etfTer, setEtfTer] = useState(0.2);
  const [etfStrategy, setEtfStrategy] = useState('accumulation');
  const [selectedEtfId, setSelectedEtfId] = useState('custom');

  // Modal State
  const [showModal, setShowModal] = useState(false);

  // --- LOGIC ---
  const formatEuro = (num) =>
    new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(num);

  const estimateRendita = () =>
    setRenditaCatastale(Math.round((Number(propertyPrice) || 0) * 0.004));
  const estimateNotary = () =>
    setNotaryFee(Math.round(1800 + (Number(propertyPrice) || 0) * 0.005));
  const estimateMaintenance = () =>
    setMaintenanceYearly(Math.round((Number(propertyPrice) || 0) * 0.005));

  const estimateImu = () => {
    if (isFirstHome) setImuYearly(0);
    else
      setImuYearly(
        Math.round((Number(renditaCatastale) || 0) * 1.05 * 160 * 0.0106)
      );
  };
  useEffect(() => estimateImu(), [renditaCatastale, isFirstHome]);

  const handleEtfPresetChange = (e) => {
    const id = e.target.value;
    setSelectedEtfId(id);
    if (id !== 'custom') {
      const preset = ETF_PRESETS.find((p) => p.id === id);
      if (preset) {
        setEtfReturn(preset.ret);
        setEtfTer(preset.ter);
      }
    }
  };

  const handleManualEtfChange = (val, type) => {
    setSelectedEtfId('custom');
    if (type === 'return') setEtfReturn(val);
    if (type === 'ter') setEtfTer(val);
  };

  const simulation = useMemo(() => {
    // 1. DATA SANITIZATION
    const _savings = Number(savings) || 0;
    const _years = Number(years) && Number(years) > 0 ? Number(years) : 1;
    const _propertyPrice = Number(propertyPrice) || 0;
    const _renovationCost = Number(renovationCost) || 0;
    const _renditaCatastale = Number(renditaCatastale) || 0;
    const _agencyRate = Number(agencyRate) || 0;
    const _notaryFee = Number(notaryFee) || 0;
    const _bankFees = Number(bankFees) || 0;
    const _downPaymentPerc = Number(downPaymentPerc) || 0;
    const _mortgageRate = Number(mortgageRate) || 0;
    const _mortgageYears = Number(mortgageYears) || 0;
    const _monthlyRent = Number(monthlyRent) || 0;
    const _condoFees = Number(condoFees) || 0;
    const _maintenanceYearly = Number(maintenanceYearly) || 0;
    const _imuYearly = Number(imuYearly) || 0;
    const _tenantRiskMonths = Number(tenantRiskMonths) || 0;
    const _propertyAppreciation = Number(propertyAppreciation) || 0;
    const _inflation = Number(inflation) || 0;
    const _etfReturn = Number(etfReturn) || 0;
    const _etfTer = Number(etfTer) || 0;

    // A. COSTS
    const agencyFeeNet = _propertyPrice * (_agencyRate / 100);
    const agencyFeeVat = agencyFeeNet * 0.22;
    const agencyTotal = agencyFeeNet + agencyFeeVat;
    const coefficient = isFirstHome ? 115.5 : 126;
    const taxRate = isFirstHome ? 0.02 : 0.09;
    const purchaseTax = Math.max(
      1000,
      _renditaCatastale * coefficient * taxRate
    );

    // SUNK COSTS (Costi a fondo perduto)
    const sunkCosts = agencyTotal + _notaryFee + purchaseTax + _bankFees;

    const mortgageAmount = _propertyPrice * (1 - _downPaymentPerc / 100);
    const downPaymentCash = _propertyPrice - mortgageAmount;

    const totalEntryCash = downPaymentCash + sunkCosts + _renovationCost;

    // B. MORTGAGE
    const monthlyRate = _mortgageRate / 100 / 12;
    const numberOfPayments = _mortgageYears * 12;
    let mortgageRata = 0;
    if (numberOfPayments > 0) {
      if (monthlyRate > 0) {
        mortgageRata =
          (mortgageAmount * monthlyRate) /
          (1 - Math.pow(1 + monthlyRate, -numberOfPayments));
      } else {
        mortgageRata = mortgageAmount / numberOfPayments;
      }
    }

    // C. LOOPS
    let currentPrice = _propertyPrice + _renovationCost * 0.7;
    let currentRent = _monthlyRent;
    let accumulatedCashflow = 0;
    let remainingDebt = mortgageAmount;
    let etfBalance = totalEntryCash;
    let accumulatedEtfCash = 0;

    const data = [];
    let initialMonthlyCashflow = 0;
    let totalEvictionCost = 0;
    let breakEvenYear = null;
    let cashflowPositiveYear = null;

    const etfAnnualNetYieldPercent = _etfReturn - _etfTer;
    const firstYearEtfIncome =
      totalEntryCash * (etfAnnualNetYieldPercent / 100) * 0.74;

    // MAIN LOOP
    for (let y = 1; y <= _years; y++) {
      let netRE = 0;
      let netCashflowYear = 0;
      let details = {
        rent: 0,
        tax: 0,
        mortgage: 0,
        expenses: 0,
        eviction: 0,
        initialCosts: null,
      };

      // --- RENT STRATEGY ---
      const rentAnnual = currentRent * 12;
      const taxRent = rentAnnual * (cedolareSecca / 100);
      const mortgageAnnual = mortgageRata * 12;

      // ANNUALIZED EVICTION RISK (5 YEARS BASE)
      let evictionLoss = 0;
      if (_tenantRiskMonths > 0) {
        evictionLoss = (currentRent * _tenantRiskMonths) / 5; // Spread over 5 years
        totalEvictionCost += evictionLoss;
      }

      const condoTotal = _condoFees * 12;
      const maintTotal = _maintenanceYearly;

      netCashflowYear =
        rentAnnual -
        taxRent -
        mortgageAnnual -
        condoTotal -
        maintTotal -
        _imuYearly -
        evictionLoss;

      details = {
        rent: rentAnnual,
        tax: taxRent,
        mortgage: mortgageAnnual,
        expenses: condoTotal + maintTotal + _imuYearly,
        eviction: evictionLoss,
        initialCosts:
          y === 1
            ? {
                sunk: sunkCosts,
                renovation: _renovationCost,
                downPayment: downPaymentCash,
              }
            : null,
      };

      // FIX: define currentMonthlyCF here, BEFORE usage in if condition
      const currentMonthlyCF = netCashflowYear / 12;

      if (y === 1) initialMonthlyCashflow = currentMonthlyCF;

      if (
        initialMonthlyCashflow < 0 &&
        currentMonthlyCF > 0 &&
        !cashflowPositiveYear
      ) {
        cashflowPositiveYear = y;
      }

      accumulatedCashflow += netCashflowYear;

      const payments = y * 12;
      if (payments < numberOfPayments) {
        remainingDebt =
          (mortgageAmount *
            (Math.pow(1 + monthlyRate, numberOfPayments) -
              Math.pow(1 + monthlyRate, payments))) /
          (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
      } else remainingDebt = 0;

      currentPrice *= 1 + _propertyAppreciation / 100;
      if (applyIstat) currentRent *= 1 + _inflation / 100;

      const sellFee = currentPrice * 0.03 * 1.22;
      let capGainTaxRE = 0;
      if (y < 5 && !isFirstHome) {
        const costBasis =
          _propertyPrice +
          _renovationCost +
          _notaryFee +
          purchaseTax +
          agencyTotal;
        const gain = currentPrice - costBasis;
        if (gain > 0) capGainTaxRE = gain * 0.26;
      }

      // Net RE = Equity (Value - Debt) + Accumulated Cash
      const reLiquidationValue =
        currentPrice - sellFee - capGainTaxRE - remainingDebt;
      netRE = reLiquidationValue + accumulatedCashflow;

      // --- ETF LOGIC ---
      const grossGrowth = _etfReturn / 100;
      const terCost = _etfTer / 100;
      const netGrowthRate = grossGrowth - terCost;

      if (etfStrategy === 'accumulation') {
        etfBalance *= 1 + netGrowthRate;
      } else {
        const annualGrossGain = etfBalance * netGrowthRate;
        const annualTax = annualGrossGain * 0.26;
        const annualNetPocket = annualGrossGain - annualTax;
        accumulatedEtfCash += annualNetPocket;
      }

      let netETF = 0;
      if (etfStrategy === 'accumulation') {
        const etfGain = etfBalance - totalEntryCash;
        const etfTaxCalc = etfGain > 0 ? etfGain * 0.26 : 0;
        netETF = etfBalance - etfTaxCalc;
      } else {
        netETF = etfBalance + accumulatedEtfCash;
      }

      if (netRE > netETF && !breakEvenYear) breakEvenYear = y;

      data.push({
        year: y,
        wealthRE: Math.round(netRE),
        wealthETF: Math.round(netETF),
        diff: Math.round(netRE - netETF),
        netCashflow: Math.round(netCashflowYear),
        details: details,
        gainRE: Math.round(netRE - totalEntryCash),
        gainETF: Math.round(netETF - totalEntryCash),
        liquidationValueRE: Math.round(reLiquidationValue),
        accumulatedRent: Math.round(accumulatedCashflow),
      });
    }

    const lastPoint =
      data.length > 0
        ? data[data.length - 1]
        : {
            wealthRE: 0,
            wealthETF: 0,
            diff: 0,
            liquidationValueRE: 0,
            accumulatedRent: 0,
          };

    // ROE Calculation
    const roiRE =
      ((lastPoint.wealthRE - totalEntryCash) / totalEntryCash) * 100;
    const roiETF =
      ((lastPoint.wealthETF - totalEntryCash) / totalEntryCash) * 100;

    return {
      initialCash: totalEntryCash,
      agencyTotal,
      purchaseTax,
      mortgageRata,
      monthlyCashflowStart: initialMonthlyCashflow,
      breakEvenYear,
      cashflowPositiveYear,
      finalDiff: lastPoint.diff,
      finalRE: lastPoint.wealthRE,
      finalETF: lastPoint.wealthETF,
      totalEvictionCost,
      firstYearEtfIncome,
      data,
      downPaymentAmount: downPaymentCash,
      sunkCosts, // Exposed for breakdown
      renovationCost: _renovationCost, // Exposed
      roiRE,
      roiETF,
      finalLiquidation: lastPoint.liquidationValueRE,
      finalRentAcc: lastPoint.accumulatedRent,
    };
  }, [
    savings,
    years,
    propertyPrice,
    renovationCost,
    renditaCatastale,
    isFirstHome,
    agencyRate,
    notaryFee,
    bankFees,
    downPaymentPerc,
    mortgageRate,
    mortgageYears,
    monthlyRent,
    condoFees,
    maintenanceYearly,
    cedolareSecca,
    imuYearly,
    tenantRiskMonths,
    propertyAppreciation,
    inflation,
    applyIstat,
    etfReturn,
    etfTer,
    etfStrategy,
    strategy,
  ]);

  const CustomCashflowTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const det = data.details;
      return (
        <div className="bg-slate-800 text-white p-3 rounded-lg shadow-xl border border-slate-700 text-[10px] z-50 w-52">
          <p className="font-bold mb-2 border-b border-slate-600 pb-1 text-xs">
            Dettaglio Anno {data.year}
          </p>
          <div className="space-y-1">
            <div className="flex justify-between">
              <span className="text-emerald-400">Entrate (Affitto):</span>
              <span>+{formatEuro(det.rent)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-rose-400">Tasse:</span>
              <span>-{formatEuro(det.tax)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-rose-400">Mutuo:</span>
              <span>-{formatEuro(det.mortgage)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-rose-400">Spese & IMU:</span>
              <span>-{formatEuro(det.expenses)}</span>
            </div>
            {det.eviction > 0 && (
              <div className="flex justify-between font-bold bg-rose-900/50 p-1 rounded mt-1">
                <span className="text-rose-300">Fondo Morosità:</span>
                <span>-{formatEuro(det.eviction)}</span>
              </div>
            )}
            <div className="border-t border-slate-600 pt-1 mt-1 flex justify-between font-bold text-xs">
              <span>Totale Netto:</span>
              <span
                className={
                  data.netCashflow >= 0 ? 'text-emerald-400' : 'text-rose-400'
                }
              >
                {formatEuro(data.netCashflow)}
              </span>
            </div>
            {det.initialCosts && (
              <div className="mt-3 pt-2 border-t border-slate-500 border-dashed">
                <p className="text-[9px] text-slate-400 mb-1 uppercase font-bold">
                  Investimento Iniziale (Fuori Grafico)
                </p>
                <div className="flex justify-between text-slate-300">
                  <span>Notaio/Agenzia/Tasse:</span>
                  <span>-{formatEuro(det.initialCosts.sunk)}</span>
                </div>
                <div className="flex justify-between text-slate-300">
                  <span>Anticipo + Ristrutt.:</span>
                  <span>
                    -
                    {formatEuro(
                      det.initialCosts.downPayment + det.initialCosts.renovation
                    )}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  const CustomWealthTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-slate-900 text-white p-3 rounded-lg shadow-xl border border-slate-700 text-xs z-50 min-w-[240px]">
          <p className="font-bold mb-2 border-b border-slate-600 pb-1">
            Anno {data.year}
          </p>
          {data.year === 1 && data.gainRE < 0 && (
            <div className="bg-rose-900/40 p-1.5 rounded mb-2 border border-rose-800/50">
              <p className="text-[9px] text-rose-200 leading-tight">
                ⚠ Il patrimonio scende subito perché hai pagato costi a fondo
                perduto (Notaio, Agenzia, Tasse) non recuperabili vendendo
                subito.
              </p>
            </div>
          )}
          <div className="mb-4 space-y-1.5">
            <p className="text-blue-400 font-bold flex justify-between border-b border-slate-700 pb-1">
              <span>Immobiliare (Totale):</span>
              <span>{formatEuro(data.wealthRE)}</span>
            </p>
            <div className="pl-2 border-l-2 border-slate-700 space-y-1 text-[10px] text-slate-300">
              <div className="flex justify-between">
                <span>Valore Casa (Netto Vendita):</span>
                <span>{formatEuro(data.liquidationValueRE)}</span>
              </div>
              <div className="flex justify-between">
                <span>
                  {data.accumulatedRent >= 0
                    ? 'Affitti Netti Accumulati:'
                    : 'Liquidità Immessa (Perdite):'}
                </span>
                <span
                  className={
                    data.accumulatedRent >= 0
                      ? 'text-emerald-400'
                      : 'text-rose-400'
                  }
                >
                  {data.accumulatedRent >= 0 ? '+' : ''}
                  {formatEuro(data.accumulatedRent)}
                </span>
              </div>
            </div>
            <p className="text-[10px] text-slate-400 flex justify-between pt-1">
              <span>Guadagno Netto Totale:</span>
              <span
                className={
                  data.gainRE >= 0 ? 'text-emerald-400' : 'text-rose-400'
                }
              >
                {data.gainRE >= 0 ? '+' : ''}
                {formatEuro(data.gainRE)}
              </span>
            </p>
          </div>
          <div className="space-y-1 border-t border-slate-700 pt-2">
            <p className="text-purple-400 font-bold flex justify-between">
              <span>ETF:</span>
              <span>{formatEuro(data.wealthETF)}</span>
            </p>
            <p className="text-[10px] text-slate-400 flex justify-between">
              <span>Guadagno Netto:</span>
              <span
                className={
                  data.gainETF >= 0 ? 'text-emerald-400' : 'text-rose-400'
                }
              >
                {data.gainETF >= 0 ? '+' : ''}
                {formatEuro(data.gainETF)}
              </span>
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  // INITIAL COSTS BREAKDOWN TOOLTIP COMPONENT
  const CostsBreakdownTooltip = ({ data }) => (
    <div className="group relative inline-block ml-1 print:hidden">
      <Info
        size={14}
        className="text-slate-400 hover:text-blue-600 cursor-help"
      />
      <div className="invisible group-hover:visible absolute z-50 w-64 p-3 mt-2 -ml-32 text-xs text-white bg-slate-800 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none border border-slate-700 z-[100]">
        <p className="font-bold border-b border-slate-600 pb-1 mb-2 text-center">
          Dettaglio Cassa Iniziale
        </p>
        <div className="space-y-1">
          <div className="flex justify-between">
            <span>Anticipo Casa:</span>
            <span>{formatEuro(data.downPaymentAmount)}</span>
          </div>
          <div className="flex justify-between text-orange-300">
            <span>Ristrutturazione:</span>
            <span>{formatEuro(data.renovationCost)}</span>
          </div>
          <div className="flex justify-between text-slate-400">
            <span>Notaio:</span>
            <span>{formatEuro(2000)}</span>
          </div>
          <div className="flex justify-between text-slate-400">
            <span>Agenzia:</span>
            <span>{formatEuro(data.agencyTotal)}</span>
          </div>
          <div className="flex justify-between text-slate-400">
            <span>Tasse & Banca:</span>
            <span>{formatEuro(data.sunkCosts - data.agencyTotal - 2000)}</span>
          </div>
          <div className="border-t border-slate-600 pt-1 mt-1 flex justify-between font-bold">
            <span>Totale:</span>
            <span>{formatEuro(data.initialCash)}</span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 font-sans pb-20 print:bg-white print:pb-0">
      {/* HEADER */}
      <header className="bg-white border-b border-slate-200 shadow-md print:border-none print:shadow-none">
        <div className="max-w-7xl mx-auto px-4 py-5 print:py-2">
          <div className="flex items-center gap-5">
            <div className="hidden md:flex h-14 w-14 bg-blue-600 rounded-2xl items-center justify-center text-white shadow-lg shadow-blue-200 rotate-3 transform hover:rotate-6 transition-all duration-300 print:hidden">
              <Calculator size={28} />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight flex items-center gap-3">
                Investire in 🏠 o 📈?
                <span className="hidden print:inline text-xs font-normal text-slate-500 ml-4">
                  Report generato il {new Date().toLocaleDateString()}
                </span>
              </h1>
              <p className="text-sm text-slate-500 mt-1 font-medium max-w-2xl leading-relaxed print:text-xs">
                Valuta il tuo futuro con{' '}
                <span className="text-blue-600 font-bold">Sicurezza</span> e{' '}
                <span className="text-emerald-600 font-bold">Chiarezza</span>.
                Il simulatore che svela i costi nascosti e calcola il vero
                rendimento netto.
              </p>
            </div>
            <div className="ml-auto print:hidden">
              <button
                onClick={() => setShowModal(true)}
                className="flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-lg font-bold text-sm hover:bg-slate-800 transition-all shadow-lg hover:shadow-xl"
              >
                <FileText size={16} /> Scarica Report PDF
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8 grid grid-cols-1 lg:grid-cols-12 gap-8 print:block">
        {/* --- LEFT COLUMN: INPUTS (HIDDEN ON PRINT) --- */}
        <div className="lg:col-span-5 space-y-6 print:hidden">
          {/* SECTION 1: CAPITAL & HOUSE */}
          <section className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 relative group">
            <h3 className="flex items-center gap-2 font-bold text-slate-800 mb-5 text-sm uppercase tracking-wider">
              <PiggyBank size={16} className="text-blue-600" /> Profilo &
              Immobile
            </h3>
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-5">
                <NumberInput
                  label="Capitale Disponibile"
                  value={savings}
                  onChange={setSavings}
                  symbol="€"
                />
                <NumberInput
                  label="Anni (Confronto)"
                  value={years}
                  onChange={setYears}
                />
              </div>
              <NumberInput
                label="Prezzo Acquisto"
                value={propertyPrice}
                onChange={setPropertyPrice}
                symbol="€"
                placeholder="Prezzo di rogito"
              />
            </div>
          </section>

          {/* SECTION 2: COSTS & TAXES */}
          <section className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
            <h3 className="flex items-center gap-2 font-bold text-slate-800 mb-5 text-sm uppercase tracking-wider">
              <Settings size={16} className="text-slate-500" /> Spese Accessorie
            </h3>
            <div className="space-y-5">
              <div className="bg-slate-50/50 p-4 rounded-xl border border-slate-200 mb-2">
                <label className="flex items-center gap-2 text-[10px] font-bold text-slate-600 uppercase mb-2">
                  <Hammer size={12} className="text-orange-500" /> Costo
                  Ristrutturazione{' '}
                  <InfoTooltip text="Spesa immediata per lavori. Aumenta il capitale iniziale necessario. Nel confronto, assumiamo che se non compri casa, investiresti questa somma nell'ETF." />
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-slate-400">
                    €
                  </span>
                  <NumberInput
                    value={renovationCost}
                    onChange={setRenovationCost}
                    placeholder="0"
                    className="mt-0"
                    symbol="€"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <NumberInput
                    label="Agenzia %"
                    value={agencyRate}
                    onChange={setAgencyRate}
                  />
                  <p className="text-[9px] text-slate-400 text-right mt-1 font-medium">
                    {formatEuro(simulation.agencyTotal)} (IVA incl.)
                  </p>
                </div>
                <div className="relative">
                  <NumberInput
                    label="Notaio €"
                    value={notaryFee}
                    onChange={setNotaryFee}
                  />
                  <button
                    onClick={estimateNotary}
                    className="absolute top-0 right-0 text-[9px] text-blue-600 font-bold hover:underline"
                  >
                    Stima
                  </button>
                </div>
                <NumberInput
                  label="Banca/Istrut. €"
                  value={bankFees}
                  onChange={setBankFees}
                />
              </div>
              <div className="bg-slate-50 p-3 rounded-xl border border-slate-200">
                <div className="flex justify-between items-end mb-2">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase">
                    Rendita Catastale{' '}
                    <InfoTooltip text="Trovi questo valore nella Visura Catastale. È la base imponibile per calcolare le tasse di acquisto (Registro) e di possesso (IMU)." />
                  </label>
                  <button
                    onClick={estimateRendita}
                    className="flex items-center gap-1 text-[10px] bg-white border border-slate-300 px-2 py-1 rounded text-slate-600 hover:text-blue-600 hover:border-blue-400 transition-colors"
                  >
                    <Sparkles size={10} /> Calcola Tu
                  </button>
                </div>
                <NumberInput
                  value={renditaCatastale}
                  onChange={setRenditaCatastale}
                />
                <div className="flex items-center justify-between pt-2 border-t border-slate-200/50 mt-3">
                  <span className="text-xs font-medium text-slate-700">
                    È Prima Casa? (Tasse 2% vs 9%)
                  </span>
                  <button
                    onClick={() => setIsFirstHome(!isFirstHome)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      isFirstHome ? 'bg-blue-600' : 'bg-slate-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        isFirstHome ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* SECTION 3: FINANCING */}
          <section className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 ring-1 ring-blue-50/50">
            <div className="flex justify-between items-center mb-5">
              <h3 className="flex items-center gap-2 font-bold text-slate-800 text-sm uppercase tracking-wider">
                <Calculator size={16} className="text-blue-600" /> Dettagli
                Mutuo
              </h3>
              <div className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-xs font-bold shadow-md shadow-blue-200">
                Rata: {formatEuro(simulation.mortgageRata)}/mese
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <NumberInput
                  label="Anticipo %"
                  value={downPaymentPerc}
                  onChange={setDownPaymentPerc}
                />
                <p className="text-[9px] text-slate-400 text-right mt-1 font-medium">
                  {formatEuro(simulation.downPaymentAmount)}
                </p>
              </div>
              <NumberInput
                label="Tasso %"
                value={mortgageRate}
                onChange={setMortgageRate}
                step={0.1}
              />
              <NumberInput
                label="Anni"
                value={mortgageYears}
                onChange={setMortgageYears}
              />
            </div>
            {/* MONETIZATION B: MORTGAGE */}
            <AffiliateCard
              title="Mutuo Migliore?"
              text="Confronta i tassi su MutuiOnline e risparmia sulla rata"
              link="#"
              color="emerald"
            />
          </section>

          {/* SECTION 4: INCOME & RISK */}
          <section className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
            <h3 className="flex items-center gap-2 font-bold text-slate-800 mb-5 text-sm uppercase tracking-wider">
              <Wallet size={16} className="text-emerald-600" /> Gestione & Tasse
              Affitto
            </h3>
            <div className="mb-5">
              <label className="block text-[10px] font-bold text-slate-400 uppercase mb-2">
                Regime Fiscale (Cedolare Secca){' '}
                <InfoTooltip text="Il 21% si applica ai contratti a Canone Libero (4+4). Il 10% è un'agevolazione per i contratti a Canone Concordato (3+2)." />
              </label>
              <div className="flex bg-slate-100 p-1 rounded-lg">
                <button
                  onClick={() => setCedolareSecca(21)}
                  className={`flex-1 py-2 text-xs font-bold rounded-md transition-all ${
                    cedolareSecca === 21
                      ? 'bg-white shadow text-slate-800'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  Libero (21%)
                </button>
                <button
                  onClick={() => setCedolareSecca(10)}
                  className={`flex-1 py-2 text-xs font-bold rounded-md transition-all ${
                    cedolareSecca === 10
                      ? 'bg-white shadow text-emerald-700'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  Concordato (10%)
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-5 mb-5">
              <NumberInput
                label="Affitto Lordo/Mese"
                value={monthlyRent}
                onChange={setMonthlyRent}
                symbol="€"
              />
              <div className="relative">
                <div className="absolute top-0 right-0 z-10">
                  <button
                    onClick={estimateMaintenance}
                    className="text-[9px] text-blue-600 font-bold hover:underline"
                  >
                    Auto (0.5%)
                  </button>
                </div>
                <NumberInput
                  label="Spese Annue (Manut.)"
                  value={maintenanceYearly}
                  onChange={setMaintenanceYearly}
                />
              </div>
            </div>
            <div className="bg-rose-50/50 p-4 rounded-xl border border-rose-100">
              <div className="flex justify-between items-center mb-2">
                <label className="flex items-center gap-1.5 text-[10px] font-bold text-rose-700 uppercase">
                  <ShieldAlert size={12} /> Stress Test: Morosità{' '}
                  <InfoTooltip text="Simula un accantonamento annuale per coprire eventuali mesi non pagati o sfitti. Calcolato su base 5 anni." />
                </label>
                <span className="text-[10px] font-bold bg-white border border-rose-200 px-2 py-0.5 rounded text-rose-700 shadow-sm">
                  {tenantRiskMonths} Mesi / 5 anni
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="12"
                step="1"
                value={tenantRiskMonths}
                onChange={(e) => setTenantRiskMonths(Number(e.target.value))}
                className="w-full h-1.5 bg-rose-200 rounded-lg appearance-none cursor-pointer accent-rose-600"
              />
              <p className="text-[9px] text-rose-700 mt-1 italic">
                *Il costo viene spalmato anno per anno come "assicurazione" o
                fondo rischi.
              </p>
            </div>
          </section>

          {/* SECTION 5: MARKET VARIABLES */}
          <section className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
            <h3 className="flex items-center gap-2 font-bold text-slate-800 mb-5 text-sm uppercase tracking-wider">
              <TrendingUp size={16} className="text-purple-600" /> Mercato &
              Inflazione
            </h3>
            <div className="grid grid-cols-2 gap-5 mb-5">
              <div>
                <NumberInput
                  label="Inflazione %"
                  value={inflation}
                  onChange={setInflation}
                  step={0.5}
                />
                <div className="flex items-start gap-2 mt-2">
                  <input
                    type="checkbox"
                    id="applyIstat"
                    checked={applyIstat}
                    onChange={(e) => setApplyIstat(e.target.checked)}
                    className="mt-0.5 rounded text-blue-600 focus:ring-blue-500"
                  />
                  <label
                    htmlFor="applyIstat"
                    className="text-[10px] text-slate-500 leading-tight cursor-pointer"
                  >
                    <span className="font-bold text-slate-600">
                      Adeguamento ISTAT
                    </span>
                    : Se attivo, l'affitto aumenta annualmente in base
                    all'inflazione.
                  </label>
                </div>
              </div>
              <NumberInput
                label="Rivalutazione Casa %"
                value={propertyAppreciation}
                onChange={setPropertyAppreciation}
                step={0.5}
              />
            </div>

            <div className="bg-purple-50 p-4 rounded-xl border border-purple-100">
              <label className="block text-[10px] font-bold text-purple-700 uppercase mb-3 flex items-center gap-1">
                <Coins size={12} /> Strategia ETF
              </label>
              <div className="mb-4">
                <label className="block text-[9px] font-bold text-purple-600 uppercase mb-1">
                  Seleziona ETF (Dati Storici)
                </label>
                <div className="relative">
                  <BarChart3
                    size={14}
                    className="absolute left-3 top-2.5 text-purple-400"
                  />
                  <select
                    value={selectedEtfId}
                    onChange={handleEtfPresetChange}
                    className="w-full p-2 pl-9 text-xs border border-purple-200 rounded-lg bg-white text-slate-700 font-medium focus:ring-2 focus:ring-purple-500 outline-none appearance-none cursor-pointer"
                  >
                    {ETF_PRESETS.map((preset) => (
                      <option key={preset.id} value={preset.id}>
                        {preset.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={14}
                    className="absolute right-3 top-2.5 text-purple-400 pointer-events-none"
                  />
                </div>
              </div>
              <div className="flex bg-white p-1 rounded-lg border border-purple-200 mb-4 shadow-sm">
                <button
                  onClick={() => setEtfStrategy('accumulation')}
                  className={`flex-1 py-2 text-xs font-bold rounded-md transition-all flex items-center justify-center gap-2 ${
                    etfStrategy === 'accumulation'
                      ? 'bg-purple-600 text-white shadow'
                      : 'text-slate-500 hover:text-purple-700'
                  }`}
                >
                  <Repeat size={14} /> Accumulazione
                </button>
                <button
                  onClick={() => setEtfStrategy('distribution')}
                  className={`flex-1 py-2 text-xs font-bold rounded-md transition-all flex items-center justify-center gap-2 ${
                    etfStrategy === 'distribution'
                      ? 'bg-purple-600 text-white shadow'
                      : 'text-slate-500 hover:text-purple-700'
                  }`}
                >
                  <Wallet size={14} /> Distribuzione
                </button>
              </div>
              {etfStrategy === 'accumulation' ? (
                <p className="text-[10px] text-purple-800 italic mb-3 bg-purple-100 p-2 rounded">
                  Gli interessi generati vengono reinvestiti automaticamente. Le
                  tasse (26%) le paghi solo alla fine.
                </p>
              ) : (
                <div className="text-[10px] text-purple-800 italic mb-3 bg-purple-100 p-2 rounded">
                  <p className="mb-2">
                    Incassi i guadagni ogni anno (tassati subito al 26%). Il
                    capitale non cresce per interesse composto.
                  </p>
                  <div className="flex justify-between items-center bg-white/50 p-1.5 rounded border border-purple-200">
                    <span className="font-semibold flex items-center gap-1">
                      <Wallet size={10} /> Rendita Media Annua (Netta):
                    </span>
                    <span className="font-bold text-purple-700">
                      ~{formatEuro(simulation.firstYearEtfIncome)}
                    </span>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-5">
                <NumberInput
                  label="Rendimento Medio %"
                  value={etfReturn}
                  onChange={(v) => handleManualEtfChange(v, 'return')}
                  step={0.5}
                />
                <NumberInput
                  label="Costo Annuo (TER) %"
                  value={etfTer}
                  onChange={(v) => handleManualEtfChange(v, 'ter')}
                  step={0.05}
                />
              </div>
              {/* MONETIZATION A: ETF */}
              <AffiliateCard
                title="Miglior Broker PAC?"
                text="Trade republic: Zero commissioni sui piani di accumulo."
                link="https://refnocode.trade.re/3xdmr6p4"
                color="blue"
              />
            </div>
          </section>
        </div>

        {/* --- RIGHT COLUMN: RESULTS --- */}
        <div className="lg:col-span-7 space-y-6 print:w-full">
          {/* SCOREBOARD (NO OVERFLOW-HIDDEN) */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm relative z-20">
            <div className="bg-slate-50 p-4 border-b border-slate-200 rounded-t-2xl">
              <h3 className="font-bold text-slate-800 flex items-center gap-2">
                <Calculator size={18} className="text-blue-600" /> Riepilogo
                Flash
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-100">
              <div className="p-6 text-center md:rounded-bl-2xl">
                <div className="text-[10px] font-bold uppercase text-slate-400 mb-2 tracking-widest flex items-center justify-center gap-1">
                  Serve Subito <CostsBreakdownTooltip data={simulation} />
                </div>
                <div
                  className={`text-2xl font-bold mb-1 ${
                    simulation.initialCash > savings
                      ? 'text-rose-600'
                      : 'text-slate-800'
                  }`}
                >
                  {formatEuro(simulation.initialCash)}
                </div>
                <p className="text-[11px] text-slate-500 leading-tight px-2">
                  Anticipo + Ristrutt. + Tasse.{' '}
                  {simulation.initialCash > savings && (
                    <span className="text-rose-600 block mt-1">
                      Ti mancano {formatEuro(simulation.initialCash - savings)}
                    </span>
                  )}
                </p>
              </div>
              <div className="p-6 text-center">
                <div className="text-[10px] font-bold uppercase text-slate-400 mb-2 tracking-widest flex items-center justify-center gap-1">
                  Flusso Cassa Operativo (Ricorrente){' '}
                  {simulation.data.length > 0 &&
                    simulation.data.some((d) => d.netCashflow > 0) && (
                      <span className="bg-emerald-100 text-emerald-700 text-[9px] px-1 rounded">
                        Dinamico
                      </span>
                    )}
                </div>
                <div className="h-16 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={simulation.data}>
                      <RechartsTooltip
                        content={<CustomCashflowTooltip />}
                        cursor={{ fill: 'transparent' }}
                      />
                      <Bar dataKey="netCashflow" radius={[2, 2, 0, 0]}>
                        {simulation.data.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={
                              entry.netCashflow >= 0 ? '#10b981' : '#f43f5e'
                            }
                          />
                        ))}
                      </Bar>
                      <ReferenceLine y={0} stroke="#94a3b8" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-[10px] text-slate-400 leading-tight px-1 mt-1">
                  {simulation.cashflowPositiveYear
                    ? `Positivo dall'anno ${simulation.cashflowPositiveYear}`
                    : simulation.monthlyCashflowStart >= 0
                    ? 'Sempre Positivo'
                    : 'Sempre Negativo'}
                </p>
                {/* MODIFICA 1: PULSANTE AGGIUNTIVO SOTTO AL GRAFICO */}
                <div className="mt-4 pt-4 border-t border-slate-100 flex justify-center print:hidden">
                  <button
                    onClick={() => setShowModal(true)}
                    className="flex items-center gap-1.5 text-[10px] bg-slate-900 text-white px-3 py-1.5 rounded-full hover:bg-slate-800 transition-all font-bold shadow-md hover:shadow-lg"
                  >
                    <FileText size={12} /> Scarica Report PDF Gratis
                  </button>
                </div>
              </div>
              <div className="p-6 text-center bg-slate-50/50 rounded-b-2xl md:rounded-bl-none md:rounded-br-2xl">
                <div className="text-[10px] font-bold uppercase text-slate-400 mb-2 tracking-widest">
                  ROI Totale / ROE
                </div>
                <div className="flex justify-center items-end gap-2 mb-1">
                  <div className="text-right">
                    <span className="block text-[9px] font-bold text-slate-400 uppercase">
                      Casa
                    </span>
                    <span
                      className={`text-lg font-bold ${
                        simulation.roiRE > simulation.roiETF
                          ? 'text-blue-600'
                          : 'text-slate-500'
                      }`}
                    >
                      {simulation.roiRE.toFixed(0)}%
                    </span>
                  </div>
                  <div className="h-8 w-[1px] bg-slate-300 mx-1"></div>
                  <div className="text-left">
                    <span className="block text-[9px] font-bold text-slate-400 uppercase">
                      ETF
                    </span>
                    <span
                      className={`text-lg font-bold ${
                        simulation.roiETF > simulation.roiRE
                          ? 'text-purple-600'
                          : 'text-slate-500'
                      }`}
                    >
                      {simulation.roiETF.toFixed(0)}%
                    </span>
                  </div>
                </div>
                <p className="text-[11px] text-slate-500 leading-tight px-2">
                  Vince{' '}
                  <strong>{simulation.finalDiff > 0 ? 'Casa' : 'ETF'}</strong>{' '}
                  di {formatEuro(Math.abs(simulation.finalDiff))}
                </p>
              </div>
            </div>
          </div>

          {/* MAIN CHART */}
          <div className="bg-white p-6 rounded-2xl shadow-lg border border-slate-100">
            <h3 className="font-bold text-slate-800 mb-6 flex justify-between items-center">
              <span className="flex items-center gap-2">
                <TrendingUp size={20} className="text-blue-600" /> Evoluzione
                Patrimonio
              </span>
              <span className="text-[10px] font-bold bg-slate-100 px-2 py-1 rounded text-slate-500 uppercase tracking-wide">
                Valore di Liquidazione (Netto Tasse)
              </span>
            </h3>
            <div className="h-80 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={simulation.data}
                  margin={{ top: 10, right: 10, bottom: 0, left: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#f1f5f9"
                  />
                  <XAxis
                    dataKey="year"
                    stroke="#94a3b8"
                    tick={{ fontSize: 11 }}
                    tickMargin={10}
                    axisLine={false}
                    tickFormatter={(val) => `Anno ${val}`}
                  />
                  <YAxis
                    stroke="#94a3b8"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(val) => `€${val / 1000}k`}
                    axisLine={false}
                  />
                  <RechartsTooltip
                    content={<CustomWealthTooltip />}
                    cursor={{ stroke: '#94a3b8', strokeWidth: 1 }}
                  />
                  <Legend
                    iconType="circle"
                    wrapperStyle={{ paddingTop: '20px', fontSize: '12px' }}
                  />
                  <Line
                    type="monotone"
                    dataKey="wealthRE"
                    name="Immobiliare (Netto)"
                    stroke="#2563eb"
                    strokeWidth={3}
                    dot={false}
                    activeDot={{ r: 6 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="wealthETF"
                    name="ETF (Alternativa)"
                    stroke="#9333ea"
                    strokeWidth={3}
                    dot={false}
                    strokeDasharray="4 4"
                  />
                  {simulation.breakEvenYear && (
                    <ReferenceLine
                      x={simulation.breakEvenYear}
                      stroke="#10b981"
                      strokeDasharray="3 3"
                      label={{
                        position: 'top',
                        value: 'Pareggio',
                        fill: '#10b981',
                        fontSize: 10,
                        fontWeight: 'bold',
                      }}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* NARRATIVE */}
          <div className="bg-white p-5 rounded-2xl border border-blue-100 shadow-sm relative overflow-hidden">
            <div className="absolute top-0 left-0 w-1 h-full bg-blue-500"></div>
            <div className="flex gap-4 items-start">
              <div className="p-2 bg-blue-50 rounded-full text-blue-600 shrink-0">
                <Info size={20} />
              </div>
              <div>
                <strong className="block text-slate-800 text-sm font-bold uppercase mb-2">
                  Analisi Completa
                </strong>
                <p className="text-slate-600 text-sm leading-relaxed mb-3">
                  {simulation.monthlyCashflowStart < 0 ? (
                    <span>
                      <strong>Attenzione alla cassa:</strong> Il canone
                      d'affitto non basta. Per i primi anni dovrai versare{' '}
                      <strong className="text-rose-600">
                        {formatEuro(Math.abs(simulation.monthlyCashflowStart))}{' '}
                        al mese
                      </strong>{' '}
                      extra.{' '}
                    </span>
                  ) : (
                    <span>
                      <strong>Ottimo Cashflow:</strong> L'immobile si ripaga da
                      solo. Ti restano circa{' '}
                      <strong className="text-emerald-600">
                        {formatEuro(simulation.monthlyCashflowStart)}
                      </strong>{' '}
                      puliti ogni mese il primo anno.{' '}
                    </span>
                  )}
                  {simulation.cashflowPositiveYear &&
                    simulation.monthlyCashflowStart < 0 && (
                      <span>
                        Grazie all'inflazione, andrai in pareggio mensile tra{' '}
                        <strong>{simulation.cashflowPositiveYear} anni</strong>.{' '}
                      </span>
                    )}
                  <span className="block mt-2">
                    {simulation.finalDiff > 0 ? (
                      <span>
                        A livello patrimoniale, l'effetto leva del mutuo ti
                        permette di battere il mercato azionario di{' '}
                        <strong className="text-blue-600">
                          {formatEuro(simulation.finalDiff)}
                        </strong>
                        .
                      </span>
                    ) : (
                      <span>
                        A livello patrimoniale, l'ETF è più efficiente e ti
                        genera{' '}
                        <strong className="text-purple-600">
                          {formatEuro(Math.abs(simulation.finalDiff))}
                        </strong>{' '}
                        in più senza lo stress della gestione immobiliare.
                      </span>
                    )}
                  </span>
                  <span className="block mt-2 text-xs italic text-slate-400 border-t border-slate-100 pt-2">
                    * Nota: Il patrimonio immobiliare include il valore netto
                    della casa venduta (
                    {formatEuro(simulation.finalLiquidation)}){' '}
                    {simulation.finalRentAcc >= 0
                      ? ` sommato ai guadagni netti incassati dagli affitti (+${formatEuro(
                          simulation.finalRentAcc
                        )}).`
                      : ` sottratto dei soldi che hai dovuto aggiungere negli anni per coprire le spese (${formatEuro(
                          simulation.finalRentAcc
                        )}).`}
                  </span>
                </p>
                {simulation.totalEvictionCost > 0 && (
                  <div className="flex items-center gap-2 text-rose-700 text-xs bg-rose-50 px-3 py-2 rounded-lg border border-rose-100 w-fit">
                    <ShieldAlert size={14} />
                    <span>
                      Scenario Morosità: inclusi -
                      {formatEuro(simulation.totalEvictionCost)} di mancati
                      incassi + spese fisse non coperte.
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* FISCAL DEEP DIVE */}
          <FiscalDeepDive
            simulation={simulation}
            cedolare={cedolareSecca}
            isFirstHome={isFirstHome}
          />

          {/* EDUCATION MODULE */}
          <EducationalModule />
        </div>
      </main>

      {/* FOOTER & EXTRAS */}
      <footer className="max-w-7xl mx-auto px-4 text-center text-slate-400 text-[10px] py-8 border-t border-slate-200 mt-8 print:hidden">
        <p className="mb-2">
          Questo strumento è un simulatore aL solo scopo educativo. Non
          costituisce sollecitazione all'investimento ne consiglio finanziario.
          I link presenti sono affiliati.
        </p>
        <p>
          &copy; {new Date().getFullYear()} RenditaVera.it - Tutti i diritti
          riservati.
        </p>
      </footer>
      <CookieBanner />
      <LeadModal isOpen={showModal} onClose={() => setShowModal(false)} />
    </div>
  );
}
